<template>
    <Card>
        <template #content>
            <div class="space-y-4">
                <div
                    v-show="plan.highlight"
                    class="flex justify-center"
                >
                    <div
                        class="rounded-full inline-flex text-center text-xs uppercase px-4 py-1 font-semibold bg-red-600 text-white"
                    >
                        Most popular
                    </div>
                </div>

                <div>
                    <div class="text-3xl font-semibold text-center">
                        {{ plan.name }}
                    </div>
                    <div class="text-lg text-center">
                        {{ plan.description }}
                    </div>
                </div>

                <div class="text-center">
                    <span class=""> $ </span>
                    <span class="text-2xl font-medium">
                        {{ plan.price }}
                    </span>
                    <span> / {{ plan.periodLabel }} </span>
                </div>

                <div class="px-2 text-sm text-gray-800 space-y-1.5">
                    <FeatureList :features="plan.featuresDisplay" />
                </div>

                <div v-if="changePlan">
                    <ApiForm
                        method="POST"
                        :url="
                            urlFactory.makeBillingPaddlePlanUpdateUrl(plan.id)
                        "
                        ref="apiFormRef"
                        :disableCatch="false"
                    />

                    <Button
                        type="button"
                        @click="isModalOpen = true"
                        class="w-full"
                        :color="plan.highlight ? 'dark' : 'dark-outline'"
                    >
                        Change
                    </Button>

                    <ModalConfirmation
                        :show="isModalOpen"
                        :action="submit"
                        color="warning"
                        :title="`Change to ${plan.name} plan?`"
                        actionText="Yes, change"
                        :actionProcessing="apiFormRef?.loading.valueOf()"
                        @close="isModalOpen = false"
                    >
                        <template
                            #content
                            class="text-gray-500 text-center"
                        >
                            <div class="text-gray-500 text-center">
                                Are you sure you want to change your current
                                subscription plan?
                            </div>
                        </template>
                    </ModalConfirmation>
                </div>
                <div v-else>
                    <Button
                        @click="handleButtonClick()"
                        :disabled="isDisabled && !isLoading"
                        :loading="isLoading"
                        loadingText="Processing..."
                        as="a"
                        :href="
                            urlFactory.makeBillingPaddleCheckoutPageUrl(plan.id)
                        "
                        class="w-full"
                        :color="plan.highlight ? 'dark' : 'dark-outline'"
                    >
                        Subscribe
                    </Button>
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup lang="ts">
    import { nextTick, ref, type PropType } from 'vue'
    import Plan from '@/App/Plan/Entity/Plan'
    import Card from '@/Shared/Component/Card.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ModalConfirmation from '@/Shared/Component/ModalConfirmation.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import FeatureList from '@/App/Plan/Component/FeatureList.vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    const emit = defineEmits<{
        checkoutStarted: []
    }>()

    const props = defineProps({
        plan: {
            type: Object as PropType<Plan>,
            required: true,
        },
        changePlan: {
            type: Boolean,
            required: true,
        },
        isDisabled: {
            type: Boolean,
            required: true,
        },
    })

    const urlFactory = new UrlFactory()
    const isLoading = ref<boolean>(false)
    const isModalOpen = ref<boolean>(false)
    const apiFormRef = ref<ApiFormRefType>(null)

    const submit = () => {
        apiFormRef.value?.submit()?.finally(() => {
            isModalOpen.value = false
        })
    }

    const handleButtonClick = () => {
        isLoading.value = true

        nextTick(() => emit('checkoutStarted'))
    }
</script>
